/**
 * 「未来 30 天现金流」卡的**纯逻辑层**（批次 `130` · J 域首页）
 *
 * ## 为什么单独一层（照 `features/spendable.ts` 的范式）
 *
 * 页面只管"长什么样"，本文件管"三态各自该说什么话"。收益两条：
 * ① 断言无需渲染（静态渲染基座零依赖直测）；
 * ② 文案与状态的对应关系**集中在一处** —— 散在 JSX 里就会出现
 * "够了还说不够"这种只有肉眼能发现的错。
 *
 * ## 🔴 本文件**一个数都不算**
 *
 * 全部字段来自服务端 `CashCoverageResponse`：`liquidCents` / `upcomingTotalCents` /
 * `lowestCents` / `lowestDay` / `shortfallCents` / `points`。
 * 这里只做**三件事**：选文案、挑明细行、拼日期短语。
 *
 * ## 🔴 文案红线（K 域 PRD §4.6.3 + `AC-K4.16`）
 *
 * | 允许 | 禁止 |
 * |---|---|
 * | 「12 月 5 日会差 ¥1,200」——**算术结论** | 「你该少花点」「别买那个」——**动作建议** |
 * | 「需要在 12 月 5 日之前补上 ¥1,200」——补多少、什么时候，都是算出来的 | 「照这样下去明年会……」——**跨窗口预测** |
 *
 * ⇒ 本文件里**没有**"建议""应该""注意控制"这类词。缺口的处置方式（提前转账 / 推迟支出 /
 * 从别的账户挪）**留白** —— 那是用户自己的决定，系统只负责把数字说准。
 */

import { CashCoverageKind, type CashCoverageResponse } from '@jiaying/shared';

/** 卡片标题（唯一名 —— 端侧不重写、不出现第二个叫法） */
export const CASHFLOW_TITLE = '未来 30 天现金流';

/** 副标题：把**口径边界**写在脸上（"只用已经确定的账"= 不预测） */
export const CASHFLOW_SUBTITLE = '可用现金 vs 未来 30 天必付 · 只用已经确定的账';

export const CASHFLOW_LIQUID_LABEL = '可用现金';
export const CASHFLOW_UPCOMING_LABEL = '未来 30 天必付';
export const CASHFLOW_SHORTFALL_LABEL = '缺口';
export const CASHFLOW_ENOUGH_TEXT = '够付';
export const CASHFLOW_SHORTFALL_TEXT = '不够付';
export const CASHFLOW_NO_UPCOMING_TEXT = '未来 30 天没有必须付的钱';
export const CASHFLOW_NO_LIQUID_TEXT =
  '账上还没有可用的现金 —— 现金 / 储蓄卡 / 电子钱包的余额会算在这里；投资账户与预付卡不算（它们付不了账单）';
export const CASHFLOW_FX_NOTICE = '有外币金额缺少汇率，可用现金与缺口都不完整';
export const CASHFLOW_ACCOUNTS_TITLE = '可用现金（钱在哪）';
export const CASHFLOW_TREND_TITLE = '逐日余额（只列有必付的那些天）';

/** 明细行（卡片中部的一行） */
export interface CashflowLine {
  readonly key: string;
  readonly label: string;
  /** `null` ⇒ 渲染成 `—`（不适用，不是 0） */
  readonly cents: number | null;
  /** 朱红（只有"缺口"用） */
  readonly alarming?: boolean;
}

export interface CashflowAccountRow {
  readonly accountId: string;
  readonly name: string;
  readonly currency: string;
  readonly originalCents: number;
  /** `null` = 缺汇率（UI 须显示 `—` 并且**不能**参与合计） */
  readonly baseCents: number | null;
}

export interface CashflowCardModel {
  readonly title: string;
  readonly subtitle: string;
  readonly kind: CashCoverageKind;
  /** 主数值（可用现金）—— 任何三态下都有意义 */
  readonly primaryCents: number;
  /** 结论短语；`noUpcoming` 时为 `null`（没有可比的东西，不该说"够付"） */
  readonly verdict: string | null;
  /** 结论配色：`null` = 中性（不着色） */
  readonly verdictAlarming: boolean;
  readonly lines: readonly CashflowLine[];
  /** 缺口建议（**纯算术陈述**：差多少、哪天之前） */
  readonly adviceLead: string | null;
  readonly adviceCents: number | null;
  readonly emptyText: string | null;
  readonly fxNotice: string | null;
  readonly accounts: readonly CashflowAccountRow[];
  readonly trend: readonly { readonly day: string; readonly remainingCents: number }[];
}

/**
 * 把服务端响应转成卡片的展示模型。
 *
 * 🔴 **三态分支在这里穷举**（`noUpcoming` / `sufficient` / `shortfall`）——
 * 组件只按 `kind` 渲染，不再自己判"是不是负数"。
 */
export function toCashflowCard(view: CashCoverageResponse): CashflowCardModel {
  const lines: CashflowLine[] = [];

  if (view.kind !== CashCoverageKind.NoUpcoming) {
    lines.push({ key: 'upcoming', label: CASHFLOW_UPCOMING_LABEL, cents: view.upcomingTotalCents });
    /*
     * 「最低点」这一行是**本卡的核心信息**：它回答"哪一刻最紧"。
     * ⚠️ 只在有必付时出现 —— 没有必付时"最低点就是今天的余额"，写出来是噪音
     * （与 `SpendableCard` 的「含待缴 ¥0 不显示」同一条纪律）。
     */
    lines.push({
      key: 'lowest',
      label: `最低点（${view.lowestDay}）`,
      cents: view.lowestCents,
      alarming: view.kind === CashCoverageKind.Shortfall,
    });
  }

  if (view.kind === CashCoverageKind.Shortfall) {
    lines.push({
      key: 'shortfall',
      label: CASHFLOW_SHORTFALL_LABEL,
      cents: view.shortfallCents,
      alarming: true,
    });
  }

  return {
    title: CASHFLOW_TITLE,
    subtitle: CASHFLOW_SUBTITLE,
    kind: view.kind,
    primaryCents: view.liquidCents,
    verdict:
      view.kind === CashCoverageKind.NoUpcoming
        ? null
        : view.kind === CashCoverageKind.Shortfall
          ? CASHFLOW_SHORTFALL_TEXT
          : CASHFLOW_ENOUGH_TEXT,
    verdictAlarming: view.kind === CashCoverageKind.Shortfall,
    lines,
    // 🔴 只陈述"哪一天之前、补多少"，**不给处置方式**（见文件头文案红线）
    adviceLead:
      view.kind === CashCoverageKind.Shortfall ? `需要在 ${view.lowestDay} 之前补上` : null,
    adviceCents: view.kind === CashCoverageKind.Shortfall ? view.shortfallCents : null,
    emptyText: view.kind === CashCoverageKind.NoUpcoming ? CASHFLOW_NO_UPCOMING_TEXT : null,
    fxNotice: view.fxIncomplete ? CASHFLOW_FX_NOTICE : null,
    accounts: view.liquidAccounts.map((a) => ({
      accountId: a.accountId,
      name: a.name,
      currency: a.currency,
      originalCents: a.originalCents as number,
      baseCents: a.baseCents === null ? null : (a.baseCents as number),
    })),
    trend: view.points.map((p) => ({
      day: p.day,
      remainingCents: p.remainingCents as number,
    })),
  };
}

/** 可用现金为空时是否该给"没有可用现金"的提示（`kind` 与它无关 —— 没必付也该说清） */
export function shouldShowNoLiquid(view: CashCoverageResponse): boolean {
  return view.liquidCents === 0;
}
